export class GemeinkostenPreise {
    kosten_netto: number;
    mwstsatz: number;
}

export class GemeinkostenPosition {
    kosten_position_id?: number;
    position?: number;
    bezeichnung: string = '';
    artikel_id: number = null;
    artikel_me?: string;
    /** aus Artikelstamm – Aufwandsvorschlag */
    artikel_buchungskonto?: string;
    kosten_netto: number = null;
    /**
     * Buchungsseite Aufwand: S = Soll (Rechnung), H = Haben (Erstattung/Gutschrift).
     * Betrag immer positiv; Umschalten per S/H in der UI (kein Minus).
     */
    buchungsseite: 'S' | 'H' = 'S';
    /** MwSt-% – üblich 19 / 7 / 0 (Gutscheine o. Ä.). */
    mwstsatz: number = 19;
    verteilungsart: 'FAHRZEUG_TA' | 'ALLGEMEIN' | 'LABOR' | 'TIERARZT' = 'ALLGEMEIN';
    fibu_fahrzeug_id: number = null;
    fibu_honorar_ta_id?: number;
    honorar_ta_bezeichnung?: string;
    kennzeichen?: string;
    fahrzeug_bezeichnung?: string;
    kostenstelle_suffix?: string;
    sachkonto: string = null;
    fibu_aufwand_konto_id: number = null;
    konto_nr?: string;
    tierarzt_id: number = null;
    tierarzt?: string;
}

/** Übliche MwSt-Sätze Gemeinkosten. */
export const FIBU_MWST_SAETZE: { value: number; label: string }[] = [
    { value: 19, label: '19 %' },
    { value: 7, label: '7 %' },
    { value: 0, label: '0 %' },
];

/** Standard 19 %; 7 % / 0 % nur wenn explizit geliefert bzw. gewählt. */
export function normalizeMwstSatz(raw: number | null | undefined): number {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
        return 19;
    }
    const rounded = Math.round(n);
    if (rounded === 0 || rounded === 7 || rounded === 19) {
        return rounded;
    }
    return 19;
}

/** Bruttobetrag → Netto anhand MwSt-Satz (%). Bei 0 % oder ungültig: unverändert. */
export function nettoFromBrutto(brutto: number | null | undefined, mwstSatz: number | null | undefined): number | null {
    const b = Number(brutto);
    const m = Number(mwstSatz);
    if (!Number.isFinite(b) || !Number.isFinite(m) || m <= 0) {
        return Number.isFinite(b) ? b : null;
    }
    return Math.round((b / (1 + m / 100)) * 100) / 100;
}

/** Auf Cent (2 Nachkommastellen) runden. */
export function roundEuro2(value: number | null | undefined): number {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return 0;
    }
    return Math.round(n * 100) / 100;
}

/**
 * MwSt so anpassen, dass Netto + MwSt dem Beleg-Brutto entspricht (Rundungsdifferenz ±Cent).
 * MwSt-% bleibt unverändert – nur der Euro-Betrag wird korrigiert.
 */
export function mwstEuroForTargetBrutto(
    netto: number | null | undefined,
    targetBrutto: number | null | undefined,
): number {
    return roundEuro2((Number(targetBrutto) || 0) - (Number(netto) || 0));
}

/** Kfz-Kostenarten (Sachkonten) – inkl. 4580 Connectivity/MB.CHARGE, 2120 Zinsen */
export const FIBU_KOSTENARTEN: { value: string; label: string }[] = [
    { value: '4510', label: '4510 Kfz-Steuer' },
    { value: '4520', label: '4520 Kfz-Versicherung' },
    { value: '4530', label: '4530 Kfz-Betrieb' },
    { value: '4540', label: '4540 Kfz-Reparatur' },
    { value: '4580', label: '4580 Sonstige Fz / Connectivity' },
    { value: '2120', label: '2120 Kfz-Zinsen' },
];

const FIBU_KFZ_SACHKONTEN = new Set(['4510', '4520', '4530', '4540', '4580', '2120']);

export const FIBU_HONORAR_KOSTENARTEN: { value: string; label: string }[] = [
    { value: '4780', label: '4780 Honorar' },
    { value: '4760', label: '4760 Gehälter' },
];

/** Default-Kostenart bei Fahrzeugwahl: aus Artikel-buchungskonto, sonst 4530. */
export function defaultKfzSachkonto(artikelBuchungskonto?: string | null): string {
    return kfzSachkontoFromArtikel(artikelBuchungskonto) || '4530';
}

/** DATEV-Konto aus Kostenart + Fahrzeug-Suffix, z. B. 4530 + 33 → 4530.33 */
export function kfzKontoNr(sachkonto?: string | null, suffix?: string | number | null): string | null {
    const sk = (sachkonto || '').trim();
    const sf = suffix == null ? '' : String(suffix).trim();
    if (!sk || !sf) {
        return null;
    }
    return `${sk}.${sf}`;
}

/** kostenarten_map „4510=18;2120=04“ → Dict */
export function parseKostenartenMap(map?: string | null): Record<string, string> {
    const out: Record<string, string> = {};
    if (!map || typeof map !== 'string') {
        return out;
    }
    for (const part of map.split(';')) {
        const p = part.trim();
        if (!p || !p.includes('=')) {
            continue;
        }
        const [k, v] = p.split('=', 2);
        const sk = (k || '').trim();
        const sf = (v || '').trim();
        if (sk && sf) {
            out[sk] = sf;
        }
    }
    return out;
}

/**
 * Suffix für Kostenart: Override aus Mapping, sonst Haupt-Suffix am Fahrzeug.
 */
export function kfzSuffixForFahrzeug(
    f:
        | {
              kostenstelle_suffix?: string | number | null;
              kostenarten_map?: string | null;
              kostenarten?: { sachkonto?: string; kostenstelle_suffix?: string }[] | null;
          }
        | null
        | undefined,
    sachkonto?: string | null,
): string | null {
    if (!f) {
        return null;
    }
    const sk = (sachkonto || '').trim();
    const map = parseKostenartenMap(f.kostenarten_map);
    if (sk && map[sk]) {
        return map[sk];
    }
    if (sk && Array.isArray(f.kostenarten)) {
        const hit = f.kostenarten.find(x => (x?.sachkonto || '').trim() === sk);
        const s = hit?.kostenstelle_suffix;
        if (s != null && String(s).trim() !== '') {
            return String(s).trim();
        }
    }
    const main = f.kostenstelle_suffix;
    return main == null || String(main).trim() === '' ? null : String(main).trim();
}

/** true wenn Fahrzeug-Overrides (kostenarten_map / kostenarten) geladen sind. */
export function fahrzeugHatKostenartenMap(f: {
    kostenarten_map?: string | null;
    kostenarten?: unknown[] | null;
} | null | undefined): boolean {
    if (!f) {
        return false;
    }
    if (typeof f.kostenarten_map === 'string' && f.kostenarten_map.trim() !== '') {
        return true;
    }
    return Array.isArray(f.kostenarten) && f.kostenarten.length > 0;
}

/** Explizite Kfz-Kostenart aus Artikel-Stamm, sonst null. */
export function kfzSachkontoFromArtikel(artikelBuchungskonto?: string | null): string | null {
    const raw = (artikelBuchungskonto || '').trim();
    if (!raw) {
        return null;
    }
    const base = raw.includes('.') ? raw.split('.')[0] : raw;
    return FIBU_KFZ_SACHKONTEN.has(base) ? base : null;
}

/** Wie InvoiceService InvoiceVehicleContext – Textheuristik, kein Stammdaten-Flag. */
const FIBU_VEHICLE_POSITIVE = [
    'tank',
    'tanken',
    'kraftstoff',
    'diesel',
    'benzin',
    'adblue',
    'aral',
    'esso',
    'shell',
    'totalenergies',
    'mobility',
    'reifen',
    'autohaus',
    'kfz',
    'werkstatt',
    'fahrzeug',
    'pkw',
    'lkw',
    'transporter',
    'suv',
    'kennzeichen',
    'inspektion',
    'ölwechsel',
    'oelwechsel',
    'kasko',
    'kraftfahrzeugsteuer',
    'connectivity',
    'kfzconnect',
    'mb.charge',
    'mb charge',
    'charge public',
    'ladesäule',
    'ladesaeule',
    'zinsen',
    'finanzierung',
    'leasingrate',
];

const FIBU_VEHICLE_NEGATIVE = [
    'telekom',
    'telefon',
    'mobilfunk',
    'hosting',
    'software',
    'lizenz',
    'verlag',
    'jahresabschluss',
    'steuerberater',
    'arbeitsmedizin',
    'beratung',
    'normec',
    'labor',
    'wasserprobe',
    'dhl',
    'deutsche post',
    'porto',
    'cws',
    'workwear',
    'pitney',
    'frankier',
    'reinigungsdienst',
];

/** Artikel-IDs mit klarem Kfz-Bezug (Matcher; Mandant 1 + Diesel/PKW). */
const FIBU_VEHICLE_ARTIKEL_IDS = new Set([667, 22306, 22517, 22578]);

/**
 * Fahrzeug-UI nur bei Kfz-Kontext: bereits gesetztes Fahrzeug/Kostenart,
 * Kfz-Artikel, oder Text in Lieferant/Artikel/Bezeichnung (wie Extract).
 */
export function isFibuFahrzeugKontext(opts: {
    lieferantName?: string | null;
    artikelId?: number | null;
    artikelMe?: string | null;
    artikelBuchungskonto?: string | null;
    bezeichnung?: string | null;
    kommentar?: string | null;
    fibuFahrzeugId?: number | null;
    sachkonto?: string | null;
    verteilungsart?: string | null;
}): boolean {
    if (opts.fibuFahrzeugId || opts.sachkonto || opts.verteilungsart === 'FAHRZEUG_TA') {
        return true;
    }
    const bk = (opts.artikelBuchungskonto || '').trim();
    if (bk) {
        const base = bk.includes('.') ? bk.split('.')[0] : bk;
        if (FIBU_KFZ_SACHKONTEN.has(base)) {
            return true;
        }
    }
    if (opts.artikelId != null && FIBU_VEHICLE_ARTIKEL_IDS.has(Number(opts.artikelId))) {
        return true;
    }
    const blob = [opts.lieferantName, opts.artikelMe, opts.bezeichnung, opts.kommentar]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    if (!blob) {
        return false;
    }
    if (FIBU_VEHICLE_NEGATIVE.some(h => blob.includes(h))) {
        return false;
    }
    return FIBU_VEHICLE_POSITIVE.some(h => blob.includes(h));
}

/**
 * Aufwandskonto aus Artikel (+ Lieferant-Gegenkonto).
 * Gleicher Kontostamm: Lieferant-Suffix vor nacktem Artikel-Hauptkonto (z. B. 4810 → 4810.50).
 * Reinigung: normal immer 4250.01; 4250.02 nur wenn Lieferant explizit Laborgeräte (.02).
 * Unterschiedliche Stämme (Papier 4930 vs. Pitney 4810.54): Artikel gewinnt.
 */
export function preferredAufwandKontoNr(opts: {
    artikelBuchungskonto?: string | null;
    lieferantGegenkonto?: string | null;
}): string | null {
    const artikel = (opts.artikelBuchungskonto || '').trim();
    const lieferantGk = (opts.lieferantGegenkonto || '').trim();
    if (!artikel && !lieferantGk) {
        return null;
    }
    const artikelReinigung = artikel === '4250' || artikel.startsWith('4250.');
    const lieferantReinigung = lieferantGk === '4250' || lieferantGk.startsWith('4250.');
    if (artikelReinigung || (!artikel && lieferantReinigung)) {
        if (lieferantGk === '4250.02' || lieferantGk.startsWith('4250.02')) {
            return '4250.02';
        }
        if (/^4250\.\d+$/.test(lieferantGk) && lieferantGk !== '4250.00') {
            return lieferantGk;
        }
        return '4250.01';
    }
    if (!artikel) {
        return lieferantGk;
    }
    if (!lieferantGk) {
        return artikel;
    }
    const artBase = artikel.split('.')[0];
    const gkBase = lieferantGk.split('.')[0];
    if (artBase === gkBase) {
        const artHasSuffix = /\.\d+$/.test(artikel) && !artikel.endsWith('.00');
        const gkHasSuffix = /\.\d+$/.test(lieferantGk) && !lieferantGk.endsWith('.00');
        if (gkHasSuffix && !artHasSuffix) {
            return lieferantGk;
        }
        if (artHasSuffix) {
            return artikel;
        }
        return lieferantGk;
    }
    // Externes Labor: Artikel oft 3400, Lieferant 4900.xx → Lieferant
    if (artBase === '3400' && gkBase === '4900') {
        return lieferantGk;
    }
    // Telefon/DSL: Artikel TEL00 oft 3400, Lieferant 4920.xx → Lieferant
    if (artBase === '3400' && gkBase === '4920') {
        return lieferantGk;
    }
    return artikel;
}

export class Gemeinkosten {
    kosten_id: number;
    kommentar: string;
    rechnung_nr: string;
    datum: Date;
    artikel_id: number;
    artikel_me: string;
    lieferant: string;
    lieferant_id: number;
    preise: GemeinkostenPreise[] = [];
    positionen: GemeinkostenPosition[] = [];
    tierarzt_id: number;
    tierarzt: string;
    labor: number;
    allgemein: number;
    leistungsdatum_von: Date;
    leistungsdatum_bis: Date;
    erstellt: Date;
    erstellt_von: number;
    editiert: Date;
    editiert_von: number;
    pdf_id: number;
    primanota_id: number;
    freigegeben: number;
    scan_review: number;
    invoice_draft_id?: number;
    wawi_run_id?: string;
    /** Summenfeld aus Listen-API (nicht am Schreib-Modell). */
    kosten_netto_gesamt?: number;
}

